import { useMemo, type ReactNode } from 'react'
import { motion } from 'framer-motion'
import type { LucideIcon } from 'lucide-react'
import leapMark from '@/assets/favicon.png'
import { cn } from '@/utils/cn'

export type HeroBadge = {
  label: string
  accent?: boolean
  live?: boolean
  icon?: LucideIcon
}

export type PageHeroProps = {
  title?: string
  subtitle?: ReactNode
  greetingName?: string
  badges?: HeroBadge[]
  actions?: ReactNode
  meta?: ReactNode
  compact?: boolean
}

function greetingForHour(hour: number) {
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.06 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
  }),
}

export function PageHero({
  title,
  subtitle,
  greetingName,
  badges = [],
  actions,
  meta,
  compact = false,
}: PageHeroProps) {
  const greeting = useMemo(() => greetingForHour(new Date().getHours()), [])
  const heading = greetingName ? `${greeting}, ${greetingName}` : title

  return (
    <header className={cn('dashboard-hero relative overflow-hidden', compact ? 'mb-0' : 'mb-1')}>
      <div className="dashboard-hero__orb dashboard-hero__orb--a" aria-hidden />
      <div className="dashboard-hero__orb dashboard-hero__orb--b" aria-hidden />
      <div className="dashboard-hero__mesh" aria-hidden />

      <div className="relative z-[1] flex flex-col gap-5 px-5 py-6 md:flex-row md:items-stretch md:gap-8 md:px-7 md:py-8">
        <motion.div
          custom={0}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="dashboard-hero__logo-card shrink-0 self-start"
        >
          <img src={leapMark} alt="LEAP" className="h-10 w-10 object-contain sm:h-12 sm:w-12" />
        </motion.div>

        <div className="min-w-0 flex-1">
          {badges.length > 0 ? (
            <motion.div custom={1} variants={fadeUp} initial="hidden" animate="show">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                {badges.map((badge) => {
                  const Icon = badge.icon
                  return (
                    <span
                      key={badge.label}
                      className={cn(
                        'dashboard-hero-badge',
                        badge.accent && 'dashboard-hero-badge--accent',
                        badge.live && 'dashboard-hero-badge--live',
                      )}
                    >
                      {badge.live ? <span className="dashboard-live-pulse" aria-hidden /> : null}
                      {Icon ? <Icon className="size-3" aria-hidden /> : null}
                      {badge.label}
                    </span>
                  )
                })}
              </div>
            </motion.div>
          ) : null}

          {heading ? (
            <motion.div custom={2} variants={fadeUp} initial="hidden" animate="show">
              <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl md:text-4xl">{heading}</h1>
            </motion.div>
          ) : null}

          {subtitle ? (
            <motion.div custom={3} variants={fadeUp} initial="hidden" animate="show">
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground md:text-base">{subtitle}</p>
            </motion.div>
          ) : null}

          {meta ? (
            <motion.div custom={4} variants={fadeUp} initial="hidden" animate="show" className="mt-4">
              {meta}
            </motion.div>
          ) : null}
        </div>

        {actions ? (
          <motion.div
            custom={5}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="flex shrink-0 flex-wrap items-start gap-2 md:justify-end"
          >
            {actions}
          </motion.div>
        ) : null}
      </div>
    </header>
  )
}

/** @deprecated Use PageHero */
export const DashboardHero = PageHero
